/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * 所有提现相关接口服务
 */
import store from '@/store';
import request from '@hb/sharp-request';
import requestDefault from '@/shared/utils/request';
import { landData } from '@/shared/utils/landData';
import { newGatewayUrl } from '@/constant';
import platform from '@/shared/utils/platform';
import { SceneTypeEnum } from '@/enums/scene-type';
import type { LoanGpsEnum } from '@/views_v2/apply-product/types';
import { CouponTypeEnum } from '@/api/coupon/types';
import type {
  ApplyChannelData,
  ApplyProductData,
  LoanUsageOption,
  RemoteContract,
  SubmitApplyResultData,
  SmsConfirmResult,
  CheckBankCardCanUse,
  AlipayCheckStatus,
  SignAlipay,
  OrderQueryRealTime,
  OrderCheck,
  AccountType,
  PayType,
  RouteMarketEnum,
  LoanModelEnum,
  CalcPlanListData,
  getLoanContractParams,
  AllinpayCheckRequestType,
  AllinpayCheckStatus,
  AllinpayAuthRequestType,
  AllinpayAuthStatus,
  LoanApplyMarketReqType,
  pageSceneEnum,
  getRepayPlansParams,
  QuoteLimitRequestType,
  UserSceneEnum,
  GetApplyProductParams,
} from './types';

/**
 * 获取可提现的渠道列表
 * @param pageScene 渠道场景值
 * @param recallPreCredit 是否预授信召回挽留流程
 * @returns
 */
export const getChannelList = (
  pageScene?: pageSceneEnum,
  recallPreCredit?: boolean,
): Promise<{
  productCreditResultList: ApplyChannelData[];
  totalAvailableAmount: string;
  gray: number // 1 灰度实验 2 非灰度对比组
  /**
   * 对优劣质用户进行导流，判断进入实验1且额度隔离
   * @see https://metis2.hellobike.cn/browse/SKXDZC-9739
   */
  optimizationNoAvailableProduct?: boolean;
  /** 用户场景 */
  scene: UserSceneEnum;
  /** 扩展字段（用于提现申请透传） */
  extendObj: unknown;
}> => request.postGate(
  'f.zmoney.withdraw.channels',
  {
    cityInfoJson: store.state.base.cityInfo,
    deviceJson: store.getters['base/devicesJSON'],
    pageScene,
    recallPreCredit,
    /** 用于标识：是否可 臻享会员、20+权益 */
    bizVersion: '1.0.1',
  },
  {
    baseURL: newGatewayUrl,
    showLoading: undefined,
  },
);

// 根据渠道的产品id获取对应的产品信息
export const getApplyProduct = (
  params: GetApplyProductParams,
): Promise<ApplyProductData> => requestDefault
  .postGate<ApplyProductData>(
    'f.loan.order.v4.withdraw',
    {
      ...platform.currentPlatformType,
      ...params,
      source: landData.getSouce(),
    },
    {
      /** TODO: 待排查提交订单 && 活体成功返回后，触发了此请求异常命中冷静期，按理此时不应该请求此接口，待后续排查优化 */
      showErrMsg: undefined,
      /** fix: 离开提现页不去给store相关的数据赋值，因为会导致试算 */
      abortXhr: true,
    },
  );

// 根据借款进行还款试算
export const calcRepayPlans = (params: getRepayPlansParams):
Promise<CalcPlanListData> => requestDefault.postGate(
  'f.loan.bill.calc',
  { ...params },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

// 查询支付宝签约状态
export const checkAlipayStatus = (productId: string):
Promise<boolean> => requestDefault.postGate<boolean>(
  'f.alipay.old.sign.status',
  {
    productId,
  },
);

// 新银河-查询支付宝签约
export const checkAlipayApply = (params: {
  productId: string,
  scene: SceneTypeEnum,
  creditApplyId?: string, // 授信id 借款场景传入
  loanOrderId?: string, // 订单号 还款场景传入
}): Promise<AlipayCheckStatus> => requestDefault.postGate<AlipayCheckStatus>('f.alipay.new.sign.query', {
  ...params,
}, { showLoading: undefined });

// 新银河-签约申请
export const getAlipaySign = (params: {
  productId: string,
  scene: SceneTypeEnum,
  creditApplyId?: string, // 授信id 借款场景传入
  loanOrderId?: string, // 订单号 还款场景传入
}): Promise<SignAlipay> => requestDefault.postGate<SignAlipay>('f.alipay.new.sign.apply', {
  ...params,
});

// 查询借款用途字典列表
export const getLoanUsageList = (): Promise<LoanUsageOption[]> => request.postGate('f.common.dict.list', {
  catalog: 'loanPurpose',
}, { showLoading: undefined, baseURL: newGatewayUrl });

// 获取提现协议列表
export const getLoanContracts = async (params: getLoanContractParams,
): Promise<RemoteContract[]> => {
  const contractResult = await request.postGate<RemoteContract[]>(
    'f.contract.mgetCommodity',
    {
      /** 新代码8180渠道请求协议可能会获取到creditRouteChannel（征信路由）字段
       * 入参此参数可区分用户请求代码的新老版本（万分之3用户访问老版本）
       * 稳定访问新代码后可删除
       */
      getCreditRouteChannel: true,
      ...params,
    },
    {
      baseURL: newGatewayUrl,
    },
  );

  if (contractResult && contractResult.length) {
    contractResult.forEach((item) => {
      item.isThirdUrl = true;
    });
  }

  return contractResult;
};

// 获取通联绑卡协议
export const getAllinpayContractUrl = (params: getLoanContractParams):
Promise<RemoteContract[]> => requestDefault
  .postGate<RemoteContract[]>('f.contract.bind.card.allinpay', {
    ...params,
  });

// 判断给定渠道在提现环节是否需要输入交易密码
export const checkNeedTradePasswd = (commodityId: string): Promise<boolean> => requestDefault.postGate('f.loan.trade.need.pwd', { commodityId });

// 提交提现申请
export const submitOrderApply = (params: {
  // 金额
  amount: number | string;
  // 期限
  applyPeriod: number | string;
  // 申请期限单位
  applyPeriodUnit: string;
  // 借款用途
  loanUsage: string;
  // 交易密码
  passwordToken: string;
  // 产品ID
  productId: string;
  // 银行卡id
  userBankCardId: string | undefined;
  // 短信验证码
  smsCode?: string;
  // 优惠券id以及优惠券类型
  couponId?: string;
  couponType?: string;
  freeCouponType?: CouponTypeEnum | '';
  // 下单前checkId，由上一次提交提现申请下单检测未通过时返回
  checkId?: string;
  checkType?: string
  // 显示利率 以及 利率周期单位
  interestDisplay: number;
  interestUnitDisplay: string;
  // 还款方式
  repaymentModel: string;
  // 投保回溯码
  insuranceRelationNo?: string;
  // 试算流水号
  trialFlowNo?: string;
  /** AMC标识 */
  amcFlag?: boolean;
  // 授信流水号ID
  creditApplyId?: string;
  /** 当前账号类型，固定银行卡，后面考虑可以删除该字段 */
  accountType?: AccountType;
  /** capitalCode 资金code */
  capitalCode?: string;
  /** 渠道优惠券 id */
  externalCouponId?: string;
  /** 串行申请标识字段 （自营串行申请场景0 */
  routeMarket: RouteMarketEnum | undefined;
  /** 是否需要通联二次绑卡 */
  allinPayCheck?: boolean;
  /** 是否按期还款 */
  repayOnSchedule?: boolean;
  /** 路由渠道征信, 默认取8180提现协议数据，针对非8180提现 一起授信8180时，取授信协议数据 */
  creditRouteChannel?: string;
  /** 资金路由Id(埋点使用) */
  fundRouteResponseId?: string;
  /** 是否提现-授信 一体化，默认 false  */
  loanWithCredit?: boolean;
  /** 提现时 授信一体化 渠道 信息 */
  loanWithCreditProductList?: string[];
  loanApplyMarketReq: LoanApplyMarketReqType;
  /** 是否预授信召回挽留流程 */
  recallPreCredit: boolean;
  /** 扩展字段，前端透传过来*/
  extendObj?: Dictionary;
  /** 业务版本号 */
  bizVersion?: string;
  /** 提现模式：quick(快速提现)、normal（常规），不传默认normal */
  loanModel?: LoanModelEnum;
}): Promise<SubmitApplyResultData> => requestDefault.postGate('f.loan.v1.apply', {
  ...params,
  ...platform.currentPlatformType,
  source: landData.getSouce(),
  cityInfoJson: store.state.base.cityInfo,
  deviceJson: store.getters['base/devicesJSON'],
  /** 1.兼容臻享会员，packageType不传的情况默认为臻享卡， = 1.0.1后必传packageType */
  bizVersion: '1.0.1',
}, {
  loadingMessage: '提交中...',
  autoLoading: true,
  abortXhr: true,
});

// 发送提现短信验证码
export const sendApplySms = (params: {
  // 渠道id
  commodityId: string;
  // 申请订单id
  loanApplyId: string;
  // 绑定银行卡id
  userBankCardId: string;
  // 下单检测id
  checkId?: string;
}): Promise<void> => requestDefault.postGate('f.loan.sms.resend', params);

// 单独验证下单短信
export const confirmApplySms = (params: {
  // 订单id
  loanOrderId: string;
  // 下单时生成的放款流水id
  repaymentOrderMessageId: string;
  // 渠道产品id
  productId: string;
  // 短信验证码
  smsCode: string;
}): Promise<SmsConfirmResult> => requestDefault.postGate('f.loan.sms.confirm', {
  ...params,
  cityInfoJson: store.state.base.cityInfo,
  deviceJson: store.getters['base/devicesJSON'],
});

// 获取设备位置配置接口
export const getConfigCityItems = (params: {
  commodityId: string | number;
  keys: string[];
}): Promise<{
  gpsLoanDepend: LoanGpsEnum;
}> => request.postGate('f.commodity.config.items', { ...params }, { showLoading: undefined, baseURL: newGatewayUrl });

// 银行卡是否可用
export const isBankCardUsed = (params: {
  userBankCardId: string;
  cardUsage: string;
  productId: string;
}): Promise<CheckBankCardCanUse> => requestDefault.postGate<{
  cardNo?: string; isBind: true; productId: number
}>(
  'f.card.is.bind.third',
  {
    ...params,
  },
);

// 度小满订单失败处理
export const orderUpdateFailed = (productId: string): Promise<void> => request.postGate('f.order.update.failed', {
  productId,
}, { baseURL: newGatewayUrl, showLoading: undefined });

// 度小满第二次借款申请
export const orderApply = (productId: string): Promise<void> => request.postGate('f.order.dxm.apply', {
  productId,
}, { baseURL: newGatewayUrl });

// 度小满借款状态实时查询
export const orderQueryRealTime = (params: {loanOrderId: string, productId: string}) :
Promise<OrderQueryRealTime> => request.postGate<OrderQueryRealTime>(
  'f.order.queryRealTime', {
    ...params,
  }, { baseURL: newGatewayUrl, showLoading: undefined },
);

// 度小满中断状态查询接口
export const orderCheck = (productId: string): Promise<OrderCheck> => request.postGate<OrderCheck>('f.order.dxm.check', {
  productId,
}, { baseURL: newGatewayUrl, showLoading: undefined });

// 小象提现流程，在生成借贷订单后，资方需要短信校验
export const applyAfterSmsAuth = (productId: string, smsCode:string, orderId:string):
Promise<boolean> => requestDefault.postGate<boolean>(
  'f.loan.applyAfter.smsAuth',
  {
    productId,
    smsCode,
    orderId,
  },
);

/**
 * 银河渠道-判断资方支付方式是支付宝签约还是银行卡支付
 * 获取绑卡支持类型
 * https://yapi.hellobike.cn/project/3415/interface/api/280121
 * */
export const alipayOrBank = (params: {
  creditApplyId?: string, // 授信流水号
  loanOrderId?: string, // 提现流水号；如无授信订单号，提现订单必传
}): Promise<PayType> => requestDefault.postGate<PayType>('f.order.card.alipayOrBank', {
  ...params,
}, { showLoading: undefined });

/**
 * 提现申请s2002事件检验
 * 需求文档：http://wiki.cheyaoshicorp.com/pages/viewpage.action?pageId=365626020
 * yapi：https://yapi.hellobike.cn/project/3415/interface/api/327687
 * */
export const loanOrderCheck = (params: {
  productId?: string;
}): Promise<boolean> => requestDefault.postGate<boolean>('f.loan.order.check', {
  ...params,
  cityInfoJson: store.state.base.cityInfo,
  deviceJson: store.getters['base/devicesJSON'],
});

/**
 * 提现申请同意协议节点-上报协议同意行为
 * yapi：https://yapi.hellobike.cn/project/3415/interface/api/429293
 * action: f.report.agreement(同意协议事件上报)
 * 不需要 await，不影响后续流程
 */
export const reportAgreementAgree = (params: {
  capitalCode?: string;
  productId: string;
}): Promise<void> => requestDefault.postGate('f.report.agreement', params, {
  showLoading: undefined,
  showErrMsg: undefined,
});

/**
 * 校验是否需要通联绑卡（此前为资产接口，后续为方便调整权益绑卡配置，调整为权益侧服务）
 * 需求文档：http://wiki.hellobike.com/pages/viewpage.action?pageId=394629440
 * yapi：https://yapi.hellobike.cn/project/7020/interface/api/379234
 * */
export const getAllinpayCheckStatus = (params: AllinpayCheckRequestType):
 Promise<AllinpayCheckStatus> => requestDefault.postGate<AllinpayCheckStatus>(
   'f.marketing.rights.bind.card.loanBindCardCheckAndApply',
   {
     ...params,
   },
 );

/**
 * 通联绑卡短验（此前为资产接口，后续为方便调整权益绑卡配置，调整为权益侧服务）
 * 需求文档：http://wiki.hellobike.com/pages/viewpage.action?pageId=394629440
 * yapi：https://yapi.hellobike.cn/project/7020/interface/api/379235
 * */
export const getAllinpayAuth = (params: AllinpayAuthRequestType):
Promise<AllinpayAuthStatus> => requestDefault.postGate<AllinpayAuthStatus>(
  'f.marketing.rights.bind.card.loanBindCardAuth',
  {
    ...params,
  },
);

/**
 * 是否命中自营b限额
 * 需求文档：https://hellobike.yuque.com/yxr77w/hx3sqh/srrqg95grmp6m1tw
 * yapi：https://yapi.hellobike.cn/project/3415/interface/api/392526
 * */
export const getQuoteLimit = (params: QuoteLimitRequestType):
Promise<{result: boolean}> => requestDefault.postGate<{result: boolean}>(
  'f.loan.quote.limit',
  {
    ...params,
  },
);
